import React, { useState, useEffect } from 'react';
import './SymptomChecker.css';

interface Symptom {
  id: string;
  name: string;
  description: string;
}

interface SymptomCategory {
  name: string;
  symptoms: Symptom[];
}

interface SymptomCategories {
  [key: string]: SymptomCategory;
}

interface ProbableCondition {
  condition: string;
  probability: 'High' | 'Medium' | 'Low';
  matchingSymptoms: number;
  description: string;
}

interface CareRecommendation {
  type: 'Self-Care' | 'Monitor' | 'Schedule Appointment' | 'Urgent Care' | 'Emergency';
  recommendation: string;
  specialist?: string;
}

interface SymptomAnalysisResult {
  probableConditions: ProbableCondition[];
  severity: 'Mild' | 'Moderate' | 'Severe';
  careRecommendations: CareRecommendation[];
  analysis: string;
  disclaimer: string;
}

interface SymptomCheckerProps {
  isOpen: boolean;
  onClose: () => void;
  onAnalyze: (symptoms: string[], pincode?: string, conversationId?: string) => Promise<SymptomAnalysisResult>;
  onDone: (selectedSymptoms: string[], result: SymptomAnalysisResult) => void;
  categories: SymptomCategories | null;
  isLoading: boolean;
}

export const SymptomChecker: React.FC<SymptomCheckerProps> = ({
  isOpen,
  onClose,
  onAnalyze,
  onDone,
  categories,
  isLoading,
}) => {
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [result, setResult] = useState<SymptomAnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pincode, setPincode] = useState<string>('');

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSymptoms([]);
      setExpandedCategories([]);
      setResult(null);
      setError(null);
      setPincode('');
    }
  }, [isOpen]);

  const toggleSymptom = (symptomId: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(symptomId)
        ? prev.filter(id => id !== symptomId)
        : [...prev, symptomId]
    );
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(key => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const handleAnalyze = async () => {
    if (selectedSymptoms.length === 0) {
      setError('Please select at least one symptom');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const analysisResult = await onAnalyze(selectedSymptoms, pincode);
      setResult(analysisResult);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze symptoms. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Mild': return '#38a169';
      case 'Moderate': return '#d69e2e';
      case 'Severe': return '#e53e3e';
      default: return '#718096';
    }
  };

  const getProbabilityColor = (probability: string) => {
    switch (probability) {
      case 'High': return '#e53e3e';
      case 'Medium': return '#d69e2e';
      case 'Low': return '#38a169';
      default: return '#718096';
    }
  };

  const getCareTypeIcon = (type: string) => {
    switch (type) {
      case 'Self-Care': return '🏠';
      case 'Monitor': return '👁️';
      case 'Schedule Appointment': return '📅';
      case 'Urgent Care': return '🏥';
      case 'Emergency': return '🚨';
      default: return '💊';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="symptom-checker-overlay" onClick={onClose}>
      <div className="symptom-checker-modal" onClick={e => e.stopPropagation()}>
        <div className="symptom-checker-header">
          <h2>🩺 Symptom Checker</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {!result ? (
          <>
            <div className={`symptom-checker-body ${analyzing ? 'analyzing-state' : ''}`}>
              {/* Analyzing overlay */}
              {analyzing && (
                <div className="analyzing-overlay">
                  <div className="analyzing-content">
                    <div className="analyzing-spinner"></div>
                    <h3>🩺 Analyzing Your Symptoms...</h3>
                    <p>Please wait while we analyze your symptoms and health profile</p>
                    <div className="analyzing-skeleton">
                      <div className="skeleton-line skeleton-long"></div>
                      <div className="skeleton-line skeleton-medium"></div>
                      <div className="skeleton-line skeleton-short"></div>
                      <div className="skeleton-line skeleton-long"></div>
                      <div className="skeleton-line skeleton-medium"></div>
                    </div>
                  </div>
                </div>
              )}

              <p className="symptom-intro">
                Select the symptoms you're currently experiencing. We'll analyze them based on your health profile and provide guidance.
              </p>

              {error && <div className="symptom-error">{error}</div>}

              {isLoading ? (
                <div className="symptom-loading">Loading symptoms...</div>
              ) : categories ? (
                <div className="symptom-categories">
                  {Object.entries(categories).map(([key, category]) => (
                    <div key={key} className="symptom-category">
                      <button
                        className={`category-toggle ${expandedCategories.includes(key) ? 'expanded' : ''}`}
                        onClick={() => toggleCategory(key)}
                      >
                        <span className="category-name">{category.name}</span>
                        <span className="category-count">
                          {category.symptoms.filter(s => selectedSymptoms.includes(s.id)).length > 0 && (
                            <span className="selected-count">
                              {category.symptoms.filter(s => selectedSymptoms.includes(s.id)).length} selected
                            </span>
                          )}
                          <span className="expand-icon">{expandedCategories.includes(key) ? '−' : '+'}</span>
                        </span>
                      </button>
                      
                      {expandedCategories.includes(key) && (
                        <div className="symptom-list">
                          {category.symptoms.map(symptom => (
                            <label key={symptom.id} className="symptom-item">
                              <input
                                type="checkbox"
                                checked={selectedSymptoms.includes(symptom.id)}
                                onChange={() => toggleSymptom(symptom.id)}
                              />
                              <div className="symptom-info">
                                <span className="symptom-name">{symptom.name}</span>
                                <span className="symptom-desc">{symptom.description}</span>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="symptom-error">Failed to load symptoms. Please try again.</div>
              )}

              {/* Pincode input */}
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', color: '#4a5568', marginBottom: '0.25rem' }}>
                  Enter your PIN code (optional)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g., 560001"
                  disabled={analyzing}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    width: '100%',
                    opacity: analyzing ? 0.5 : 1
                  }}
                />
              </div>
            </div>

            <div className={`symptom-checker-footer ${analyzing ? 'footer-disabled' : ''}`}>
              <div className="selected-summary">
                {selectedSymptoms.length} symptom{selectedSymptoms.length !== 1 ? 's' : ''} selected
              </div>
              <div className="footer-actions">
                <button className="btn-secondary" onClick={onClose} disabled={analyzing}>Cancel</button>
                <button
                  className="btn-primary"
                  onClick={handleAnalyze}
                  disabled={selectedSymptoms.length === 0 || analyzing}
                >
                  {analyzing ? '🔄 Analyzing...' : 'Analyze Symptoms'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="symptom-checker-body result-view">
              <div className="severity-banner" style={{ backgroundColor: getSeverityColor(result.severity) }}>
                <span className="severity-label">Severity Assessment:</span>
                <span className="severity-value">{result.severity}</span>
              </div>

              <div className="analysis-section">
                <h3>📋 Analysis</h3>
                <p className="analysis-text">{result.analysis}</p>
              </div>

              {result.probableConditions.length > 0 && (
                <div className="conditions-section">
                  <h3>🔍 Probable Conditions</h3>
                  <div className="conditions-list">
                    {result.probableConditions.map((condition, index) => (
                      <div key={index} className="condition-card">
                        <div className="condition-header">
                          <span className="condition-name">{condition.condition}</span>
                          <span
                            className="condition-probability"
                            style={{ backgroundColor: getProbabilityColor(condition.probability) }}
                          >
                            {condition.probability} Match
                          </span>
                        </div>
                        <p className="condition-desc">{condition.description}</p>
                        <span className="matching-symptoms">
                          {condition.matchingSymptoms} matching symptom{condition.matchingSymptoms !== 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.careRecommendations.length > 0 && (
                <div className="recommendations-section">
                  <h3>💡 Care Recommendations</h3>
                  <div className="recommendations-list">
                    {result.careRecommendations.map((rec, index) => (
                      <div key={index} className="recommendation-card">
                        <div className="rec-header">
                          <span className="rec-icon">{getCareTypeIcon(rec.type)}</span>
                          <span className="rec-type">{rec.type}</span>
                        </div>
                        <p className="rec-text">{rec.recommendation}</p>
                        {rec.specialist && (
                          <span className="rec-specialist">Recommended: {rec.specialist}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="disclaimer-section">
                <p className="disclaimer">{result.disclaimer}</p>
              </div>
            </div>

            <div className="symptom-checker-footer">
              <button className="btn-secondary" onClick={() => setResult(null)}>
                Check Different Symptoms
              </button>
              <button className="btn-primary" onClick={() => {
                if (result) {
                  console.log('Done clicked, calling onDone with:', { selectedSymptoms, result });
                  onDone(selectedSymptoms, result);
                }
                onClose();
              }}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SymptomChecker;
